import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFormat } from "hooks/useFormat";
import Typography from "components/basic/typography";
import PolicySection from "pages/privacy/components/PolicySection";
import TableOfContents from "pages/privacy/components/TableOfContents";
import { PolicySection as Section } from "pages/privacy/content";

interface LegalDocumentProps {
  title: string;
  /** Matches the version consents store. */
  version: number;
  /** ISO date, shown in the reader's language. */
  lastUpdated: string;
  intro: React.ReactNode;
  sections: Section[];
}

/**
 * Layout shared by the legal pages: title, contents list and numbered
 * sections, at a readable measure like the privacy policy.
 */
const LegalDocument: React.FC<LegalDocumentProps> = ({
  title,
  version,
  lastUpdated,
  intro,
  sections,
}) => {
  const { t } = useTranslation("legal");
  const format = useFormat();

  return (
    <Box
      sx={{
        maxWidth: "72ch",
        margin: "0 auto",
        padding: { xs: 2, md: 5 },
        minWidth: 0,
      }}
    >
      <Typography variant="h3" style={{ fontWeight: 700 }}>
        {title}
      </Typography>

      <Typography
        variant="body2"
        sx={{ color: "text.secondary", marginBottom: 4 }}
      >
        {t("common.version_updated", {
          version,
          date: format.date(`${lastUpdated}T00:00:00`, {
            day: "numeric",
            month: "long",
            year: "numeric",
          }),
        })}
      </Typography>

      <Typography variant="body1" sx={{ marginBottom: 4 }}>
        {intro}
      </Typography>

      <TableOfContents sections={sections} />

      {sections.map((section) => (
        <PolicySection key={section.id} section={section} />
      ))}
    </Box>
  );
};

export default LegalDocument;
